import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Navigation,
  Send,
  Copy,
  MessageCircle,
  CheckCircle2,
  RotateCcw,
  MapPin,
} from "lucide-react";
import { PACKAGES } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#050914]/70 px-3 py-1.5 text-sm text-white placeholder:text-slate-500 transition-colors duration-200 focus:border-cyan-400/60 focus:outline-none";

const labelCls = "mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400";

export default function RegisterSection({ selected, onSelect }) {
  const [form, setForm] = useState({
    full_name: "",
    phone_number: "",
    address: "",
    city_district: "",
    notes: "",
  });
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const useGps = () => {
    if (!navigator.geolocation) return toast.error("Perangkat tidak mendukung GPS");
    toast.info("Mengambil lokasi GPS...");
    navigator.geolocation.getCurrentPosition(
      (g) => {
        setPos([g.coords.latitude, g.coords.longitude]);
        toast.success("Lokasi GPS ditemukan!");
      },
      () => toast.error("Gagal mengambil lokasi. Izinkan akses lokasi di browser/HP Anda."),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const copyCoords = () => {
    if (!pos) return;
    navigator.clipboard.writeText(`${pos[0]}, ${pos[1]}`);
    toast.success("Koordinat disalin");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pos)
      return toast.error("Tekan tombol 'Gunakan Lokasi Saya' untuk menandai lokasi dulu ya");
    setBusy(true);
    try {
      const res = await fetch(`${API}/installations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          package_id: selected,
          latitude: pos[0],
          longitude: pos[1],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Gagal mengirim pengajuan");
      setDone(data);
      toast.success("Pengajuan berhasil dicatat!");
    } catch (err) {
      toast.error(err.message || "Terjadi kesalahan, coba lagi");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setDone(null);
    setPos(null);
    setForm({ full_name: "", phone_number: "", address: "", city_district: "", notes: "" });
  };

  return (
    <section id="pasang" data-testid="register-section" className="relative pb-6 pt-6 sm:pb-8 sm:pt-8">
      <div className="pointer-events-none absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-[#F04E23]/10 blur-[160px]" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          className="font-mono text-xs tracking-[0.25em] text-cyan-400"
        >
          PEMASANGAN BARU
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.1 }}
          className="mt-4 font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl"
        >
          Ajukan pemasangan, kirim titik lokasimu.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.18 }}
          className="mt-4 max-w-xl text-base text-slate-300"
        >
          Isi formulir, tekan tombol "Gunakan Lokasi Saya", lalu kirim. Tim teknisi kami akan
          menghubungi Anda maksimal 1×24 jam.
        </motion.p>

        <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-2">
          <div className="h-full rounded-3xl border border-white/10 bg-[#0A1124]/80 p-4 backdrop-blur-xl sm:p-6">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="success-panel"
                className="py-6 text-center"
              >
                <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
                <h3 className="mt-4 font-display text-2xl font-bold text-white">Pengajuan Diterima!</h3>
                <p className="mt-2 text-sm text-slate-400">Nomor pengajuan Anda:</p>
                <p data-testid="success-ref-id" className="mt-1 font-mono text-2xl font-bold text-cyan-400">
                  {done.id}
                </p>
                <p className="mx-auto mt-4 max-w-sm text-sm text-slate-400">
                  Teruskan detail pengajuan ke CS kami agar pemasangan lebih cepat diproses:
                </p>
                <a
                  data-testid="btn-forward-whatsapp"
                  href={done.whatsapp_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 font-bold text-white transition-colors duration-200 hover:bg-emerald-400"
                >
                  <MessageCircle className="h-5 w-5" /> Teruskan ke WhatsApp CS
                </a>
                <div>
                  <button
                    data-testid="btn-reset-form"
                    onClick={reset}
                    className="mt-5 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors duration-200 hover:text-white"
                  >
                    <RotateCcw className="h-4 w-4" /> Buat Pengajuan Baru
                  </button>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="registration-form" className="space-y-3">
                <div>
                  <label className={labelCls}>
                    Nama Lengkap
                  </label>
                  <input
                    data-testid="input-full-name"
                    required
                    minLength={3}
                    value={form.full_name}
                    onChange={set("full_name")}
                    placeholder="Nama sesuai KTP"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Nomor WhatsApp Aktif
                  </label>
                  <input
                    data-testid="input-phone"
                    required
                    type="tel"
                    minLength={8}
                    value={form.phone_number}
                    onChange={set("phone_number")}
                    placeholder="08xxxxxxxxxx"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Alamat Lengkap Pemasangan
                  </label>
                  <textarea
                    data-testid="input-address"
                    required
                    minLength={5}
                    rows={2}
                    value={form.address}
                    onChange={set("address")}
                    placeholder="Jalan, nomor rumah, RT/RW, patokan"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Pilihan Paket
                  </label>
                  <div data-testid="package-selector" className="grid grid-cols-3 gap-2">
                    {PACKAGES.map((p) => (
                      <button
                        type="button"
                        key={p.id}
                        data-testid={`pkg-option-${p.id}`}
                        onClick={() => onSelect(p.id)}
                        className={`rounded-lg border px-2 py-2 text-center transition-colors duration-200 ${
                          selected === p.id
                            ? "border-[#F04E23] bg-[#F04E23]/15 text-white"
                            : "border-white/10 bg-[#050914]/60 text-slate-400 hover:border-white/25"
                        }`}
                      >
                        <span className="block font-display text-sm font-bold">{p.speed} Mbps</span>
                        <span className="mt-0.5 block text-[10px]">Rp {p.price}/bln</span>
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  data-testid="btn-submit-registration"
                  type="submit"
                  disabled={busy}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F04E23] py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#FF6B35] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {busy ? "Mengirim Pengajuan..." : "Kirim Pengajuan Pemasangan"}
                </button>
                <p className="text-center text-[11px] text-slate-500">
                  Data tersimpan aman & dapat diteruskan langsung ke CS Fasnet Fiber.
                </p>
              </form>
            )}
          </div>

          <div className="h-full">
            <div
              data-testid="location-card"
              className="h-full rounded-3xl border border-white/10 bg-[#0A1124]/80 p-4 backdrop-blur-xl sm:p-6"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F04E23]/15">
                  <MapPin className="h-4 w-4 text-[#FF6B35]" />
                </span>
                <div>
                  <h3 className="font-display text-base font-bold text-white">Titik Lokasi Rumah</h3>
                  <p className="text-[11px] text-slate-400">Dibutuhkan untuk survei pemasangan</p>
                </div>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-slate-300">
                Tekan tombol di bawah, lalu izinkan akses lokasi saat browser/HP Anda meminta.
                Titik koordinat otomatis ikut terkirim bersama pengajuan.
              </p>
              <button
                data-testid="btn-use-gps"
                type="button"
                onClick={useGps}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#3b82f6]"
              >
                <Navigation className="h-4 w-4" /> Gunakan Lokasi Saya
              </button>
              <div
                data-testid="coords-display"
                className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-[#050914]/70 px-3 py-2.5 font-mono text-[11px]"
              >
                <span className={pos ? "text-cyan-300" : "text-slate-500"}>
                  {pos
                    ? `${pos[0].toFixed(6)}, ${pos[1].toFixed(6)}`
                    : "Belum ada titik — tekan tombol di atas"}
                </span>
                {pos && (
                  <button
                    data-testid="btn-copy-coords"
                    type="button"
                    onClick={copyCoords}
                    className="text-slate-400 transition-colors duration-200 hover:text-white"
                    aria-label="Salin koordinat"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                )}
              </div>
              {pos && (
                <p
                  data-testid="coords-saved-hint"
                  className="mt-3 flex items-center gap-2 text-xs font-bold text-emerald-400"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Titik lokasi tersimpan, siap dikirim
                </p>
              )}
              <p className="mt-5 text-xs leading-relaxed text-slate-500">
                Privasi: koordinat hanya dipakai teknisi Fasnet untuk keperluan survei & pemasangan.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
